import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import Logo from "@/components/layout/Logo";


export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(identifier.trim(), password);
      toast.success("Welcome back!");
      if (user.firstLogin) navigate("/change-password");
      else if (user.role === "super_admin") navigate("/admin");
      else navigate("/app");
    } catch (err) {
      toast.error(apiError(err, "Invalid username or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-appbg">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900 via-brand-700 to-blue-500" />
        <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-20 right-10 h-96 w-96 rounded-full bg-blue-300/20 blur-3xl" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Logo light />
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight">
              Automate your Google Business Profile.
            </h1>
            <p className="mt-4 max-w-md text-white/80">
              Schedule posts, reply to reviews with AI, send genuine review requests, and track your local growth — all in one premium dashboard.
            </p>
            <div className="mt-8 flex gap-6 text-sm">
              <div><p className="text-2xl font-bold">AI</p><p className="text-white/70">Smart Replies</p></div>
              <div><p className="text-2xl font-bold">QR</p><p className="text-white/70">Review Tools</p></div>
              <div><p className="text-2xl font-bold">24/7</p><p className="text-white/70">Automation</p></div>
            </div>
          </div>
          <p className="text-xs text-white/60">© {new Date().getFullYear()} AWebBee.com — GMB Automation Manager</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 lg:hidden"><Logo /></div>
          <p className="text-overline">Welcome back</p>
          <h2 className="mt-2 font-heading text-3xl font-bold text-slate-900">Sign in to your account</h2>
          <p className="mt-2 text-sm text-slate-500">Enter your credentials to access your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Username or Email</label>
              <input
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="admin"
                autoComplete="username"
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200"
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 pr-11 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200"
                  required
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password-btn">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="remember-me-checkbox" /> Remember me
              </label>
              <Link to="/forgot-password" className="text-sm font-medium text-brand-600 hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="h-11 w-full rounded-xl bg-brand-700 text-white shadow-brand hover:bg-brand-800">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
            
            <p className="mt-4 text-center text-sm text-slate-600">
              Don't have an account? <Link to="/signup" className="font-medium text-brand-600 hover:underline">Sign up</Link>
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
